// vuex/BookingService/GlobalBookingStore.js
import moment from 'moment'

// State
const state = {
  booking_dates: {
    check_in: '',
    check_out: '',
    days: 0
  },
  booking_guests: {
    adults: 0,
    children: 0,
    total: 0
  },
  booking_details: {
    guests: 0,
    vehicle: null,
    currency: 'KES'
  }
}

// Getters
const getters = {
  hasValidBookingDates: (state) => {
    return !!(state.booking_dates.check_in && state.booking_dates.check_out)
  },
  
  formattedCheckIn: (state) => {
    if (!state.booking_dates.check_in) return ''
    return moment(state.booking_dates.check_in).format('MMM DD, YYYY')
  },
  
  formattedCheckOut: (state) => {
    if (!state.booking_dates.check_out) return ''
    return moment(state.booking_dates.check_out).format('MMM DD, YYYY')
  },
  
  bookingDays: (state) => {
    return state.booking_dates.days
  },
  
  getCheckIn: (state) => {
    return state.booking_dates.check_in
  },
  
  getCheckOut: (state) => {
    return state.booking_dates.check_out
  },
  
  getBookingDates: (state) => {
    return state.booking_dates
  },
  
  getMinCheckOutDate: (state) => {
    if (!state.booking_dates.check_in) return moment().format('YYYY-MM-DD')
    return moment(state.booking_dates.check_in).add(1, 'days').format('YYYY-MM-DD')
  },
  
  // Guest getters
  getAdults: (state) => {
    return state.booking_guests.adults || 0
  },
  
  getChildren: (state) => {
    return state.booking_guests.children || 0
  },
  
  getTotalGuests: (state) => {
    return state.booking_guests.total || 0
  },
  
  hasGuests: (state) => {
    return (state.booking_guests.adults + state.booking_guests.children) > 0
  },
  
  getFormattedGuests: (state) => {
    const adults = state.booking_guests.adults
    const children = state.booking_guests.children
    if (adults === 0 && children === 0) return 'No guests selected'
    return `${adults} Adult${adults !== 1 ? 's' : ''} and ${children} Child${children !== 1 ? 'ren' : ''}`
  }
}

// Mutations
const mutations = {
  SET_BOOKING_DATES(state, dates) {
    state.booking_dates = {
      check_in: dates.check_in || '',
      check_out: dates.check_out || '',
      days: dates.days || 0
    }
    
    // Save to localStorage
    localStorage.setItem('global_booking_dates', JSON.stringify(state.booking_dates))
    
    // Also update the old booking localStorage for backward compatibility
    if (dates.check_in && dates.check_out) {
      const bookingData = {
        check_in: dates.check_in,
        check_out: dates.check_out,
        days: dates.days || 0,
        currency: state.booking_details.currency
      }
      localStorage.setItem('booking', JSON.stringify(bookingData))
    }
  },
  
  UPDATE_CHECK_IN(state, check_in) {
    state.booking_dates.check_in = check_in
    if (state.booking_dates.check_in && state.booking_dates.check_out) {
      const a = moment(check_in)
      const b = moment(state.booking_dates.check_out)
      state.booking_dates.days = b.diff(a, 'days')
    }
    localStorage.setItem('global_booking_dates', JSON.stringify(state.booking_dates))
  },
  
  UPDATE_CHECK_OUT(state, check_out) {
    state.booking_dates.check_out = check_out
    if (state.booking_dates.check_in && state.booking_dates.check_out) {
      const a = moment(state.booking_dates.check_in)
      const b = moment(check_out)
      state.booking_dates.days = b.diff(a, 'days')
    }
    localStorage.setItem('global_booking_dates', JSON.stringify(state.booking_dates))
  },
  
  CLEAR_BOOKING_DATES(state) {
    state.booking_dates = {
      check_in: '',
      check_out: '',
      days: 0
    }
    localStorage.removeItem('global_booking_dates')
  },
  
  // Guest mutations
  SET_BOOKING_GUESTS(state, guests) {
    state.booking_guests = {
      adults: guests.adults || 0,
      children: guests.children || 0,
      total: (guests.adults || 0) + (guests.children || 0)
    }
    localStorage.setItem('global_booking_guests', JSON.stringify(state.booking_guests))
    
    // Also update the old guests format for backward compatibility
    localStorage.setItem('guests', JSON.stringify({
      adults: state.booking_guests.adults,
      children: state.booking_guests.children
    }))
  },
  
  UPDATE_ADULTS(state, adults) {
    state.booking_guests.adults = adults || 0
    state.booking_guests.total = state.booking_guests.adults + state.booking_guests.children
    localStorage.setItem('global_booking_guests', JSON.stringify(state.booking_guests))
  },
  
  UPDATE_CHILDREN(state, children) {
    state.booking_guests.children = children || 0
    state.booking_guests.total = state.booking_guests.adults + state.booking_guests.children
    localStorage.setItem('global_booking_guests', JSON.stringify(state.booking_guests))
  },
  
  CLEAR_BOOKING_GUESTS(state) {
    state.booking_guests = {
      adults: 0,
      children: 0,
      total: 0
    }
    localStorage.removeItem('global_booking_guests')
  },
  
  // Booking details mutations
  UPDATE_GUESTS(state, guests) {
    state.booking_details.guests = guests
    localStorage.setItem('global_booking_details', JSON.stringify(state.booking_details))
  },
  
  UPDATE_VEHICLE(state, vehicle) {
    state.booking_details.vehicle = vehicle
    localStorage.setItem('global_booking_details', JSON.stringify(state.booking_details))
  },
  
  UPDATE_CURRENCY(state, currency) {
    state.booking_details.currency = currency
    localStorage.setItem('global_booking_details', JSON.stringify(state.booking_details))
  },
  
  LOAD_SAVED_DATA(state) {
    // Load saved dates
    const savedDates = localStorage.getItem('global_booking_dates')
    if (savedDates) {
      try {
        const dates = JSON.parse(savedDates)
        if (dates.check_in && dates.check_out) {
          state.booking_dates = dates
        }
      } catch (e) {
        console.error('Error loading saved dates:', e)
      }
    }
    
    // Load saved guests
    const savedGuests = localStorage.getItem('global_booking_guests')
    if (savedGuests) {
      try {
        const guests = JSON.parse(savedGuests)
        state.booking_guests = guests
      } catch (e) {
        console.error('Error loading saved guests:', e)
      }
    }
    
    // Load saved booking details
    const savedDetails = localStorage.getItem('global_booking_details')
    if (savedDetails) {
      try {
        const details = JSON.parse(savedDetails)
        state.booking_details = { ...state.booking_details, ...details }
      } catch (e) {
        console.error('Error loading saved details:', e)
      }
    }
  }
}

// Actions
const actions = {
  // Date actions
  updateBookingDates({ commit }, dates) {
    commit('SET_BOOKING_DATES', dates)
  },
  
  updateCheckIn({ commit }, check_in) {
    commit('UPDATE_CHECK_IN', check_in)
  },
  
  updateCheckOut({ commit }, check_out) {
    commit('UPDATE_CHECK_OUT', check_out)
  },
  
  clearBookingDates({ commit }) {
    commit('CLEAR_BOOKING_DATES')
  },
  
  // Guest actions
  updateBookingGuests({ commit }, guests) {
    commit('SET_BOOKING_GUESTS', guests)
  },
  
  updateAdults({ commit }, adults) {
    commit('UPDATE_ADULTS', adults)
  },
  
  updateChildren({ commit }, children) {
    commit('UPDATE_CHILDREN', children)
  },
  
  clearBookingGuests({ commit }) {
    commit('CLEAR_BOOKING_GUESTS')
  },
  
  // Booking details actions
  updateGuests({ commit }, guests) {
    commit('UPDATE_GUESTS', guests)
  },
  
  updateVehicle({ commit }, vehicle) {
    commit('UPDATE_VEHICLE', vehicle)
  },
  
  updateCurrency({ commit }, currency) {
    commit('UPDATE_CURRENCY', currency)
  },
  
  // Load saved data on app start
  loadSavedData({ commit }) {
    commit('LOAD_SAVED_DATA')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}